import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../context/ThemeContext";
import { getThemeClasses } from "../../utils/ThemeClasses";
import { useUserStore } from "../../store/userStore";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useCompanyUnits, type ReportingUnit } from "../../features/dashboards/reporting";
import Swal from "sweetalert2";
import api from "../../utils/api";
import { fileDate } from "../../features/sg/exportRows";
import { toCsv, downloadCsv } from "../../features/sg/csv";


export default function EntregaServicioDashboard() {
  const { t } = useTranslation("entregaServicio");
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);

  const user = useUserStore((state) => state.user);
  const companyId = useCompaniesStore((state) => state.companyId);

  const userCompanyId = companyId || user?.company_id;
  const reportingQuery = useCompanyUnits(userCompanyId, { reporting: "yes" });
  const notReportingQuery = useCompanyUnits(userCompanyId, { reporting: "no" });

  const [currentPageReporting, setCurrentPageReporting] = useState(1);
  const [currentPageNotReporting, setCurrentPageNotReporting] = useState(1);
  const [searchReporting, setSearchReporting] = useState("");
  const [searchNotReporting, setSearchNotReporting] = useState("");
  const [platformFilterReporting, setPlatformFilterReporting] = useState("");
  const [platformFilterNotReporting, setPlatformFilterNotReporting] = useState("");
  const [showReportModal, setShowReportModal] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const itemsPerPage = 5;

  const reporting = reportingQuery.data?.units || [];
  const notReporting = notReportingQuery.data?.units || [];
  const allUnits = [...reporting, ...notReporting];

  const filterUnits = (units: ReportingUnit[], searchTerm: string, platformFilter: string = "") => {
    let result = units;

    // Filtrar por búsqueda
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (u) =>
          u.imei.toLowerCase().includes(term) ||
          u.user_name?.toLowerCase().includes(term) ||
          u.business_name?.toLowerCase().includes(term) ||
          u.device_name?.toLowerCase().includes(term)
      );
    }

    // Filtrar por plataforma
    if (platformFilter) {
      result = result.filter((u) => u.platform === platformFilter);
    }

    return result;
  };

  const filteredReporting = filterUnits(reporting, searchReporting, platformFilterReporting);
  const filteredNotReporting = filterUnits(notReporting, searchNotReporting, platformFilterNotReporting);

  // Calcular plataformas disponibles
  const getPlatformsForUnits = (units: ReportingUnit[]) => {
    const platforms = new Set(units.map((u) => u.platform).filter(Boolean));
    return Array.from(platforms).sort();
  };

  const platformsReporting = getPlatformsForUnits(reporting);
  const platformsNotReporting = getPlatformsForUnits(notReporting);
  const allPlatforms = Array.from(new Set([...platformsReporting, ...platformsNotReporting])).sort();

  // Contar unidades por plataforma
  const countByPlatform = (units: ReportingUnit[]) => {
    const counts: Record<string, number> = {};
    units.forEach((u) => {
      const platform = u.platform || "unknown";
      counts[platform] = (counts[platform] || 0) + 1;
    });
    return counts;
  };

  const platformCountsReporting = countByPlatform(reporting);
  const platformCountsNotReporting = countByPlatform(notReporting);

  const getReportingPage = () => {
    const startIdx = (currentPageReporting - 1) * itemsPerPage;
    return filteredReporting.slice(startIdx, startIdx + itemsPerPage);
  };

  const getNotReportingPage = () => {
    const startIdx = (currentPageNotReporting - 1) * itemsPerPage;
    return filteredNotReporting.slice(startIdx, startIdx + itemsPerPage);
  };

  const totalPagesReporting = Math.ceil(filteredReporting.length / itemsPerPage);
  const totalPagesNotReporting = Math.ceil(filteredNotReporting.length / itemsPerPage);

  const Pagination = ({ currentPage, totalPages, onPageChange }: { currentPage: number; totalPages: number; onPageChange: (page: number) => void }) => {
    if (totalPages <= 1) return null;

    return (
      <div className={`flex items-center justify-between px-4 py-3 border-t ${themeClasses.cardBorder}`}>
        <div className={`text-sm ${themeClasses.textSecondary}`}>
          Página {currentPage} de {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              currentPage === 1
                ? `${themeClasses.textSecondary} cursor-not-allowed`
                : `text-primary-500 hover:bg-primary-500/10`
            }`}
          >
            ← Anterior
          </button>
          <button
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              currentPage === totalPages
                ? `${themeClasses.textSecondary} cursor-not-allowed`
                : `text-primary-500 hover:bg-primary-500/10`
            }`}
          >
            Siguiente →
          </button>
        </div>
      </div>
    );
  };

  const TableSkeleton = () => (
    <div className="space-y-2">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className={`h-12 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded animate-pulse`}></div>
      ))}
    </div>
  );

  const handleSendReport = async () => {
    // Correos fijos que siempre reciben el reporte
    const fixedEmails = ["gcruz@dumaxst.com", "ghernandez@dumaxst.com", "andersonalexander117@gmail.com"];
    const emails = fixedEmails;

    setIsReporting(true);

    try {
      await api.post("/entregaServicio/report-not-reporting", {
        companyId: userCompanyId,
        companyName: companyName,
        emails,
        units: filteredNotReporting,
        platform: "v5",
      });

      setShowReportModal(false);

      Swal.fire({
        title: t("entregaServicio.alerts.reportSent"),
        html: `<div class="text-left"><p><strong>${filteredNotReporting.length}</strong> ${t("entregaServicio.alerts.unitReported")}</p><p><strong>📧 ${t("entregaServicio.alerts.sentTo")}</strong> ${fixedEmails.join(", ")}</p></div>`,
        icon: "success",
      });
    } catch (error: any) {
      console.error("Error enviando reporte:", error);
      Swal.fire({
        title: t("entregaServicio.alerts.error"),
        text: error.response?.data?.message || t("entregaServicio.alerts.errorMessage"),
        icon: "error",
      });
    } finally {
      setIsReporting(false);
    }
  };

  const downloadTableAsCSV = (units: ReportingUnit[], filenamePrefix: string) => {
    const today = fileDate();
    const filename = `${filenamePrefix}_${today}.csv`;

    const headers = ["IMEI", "Nombre Dispositivo", "Usuario", "Razón Social", "Plataforma", "Estado"];
    const rows = units.map((u) => [
      u.imei,
      u.device_name || "",
      u.user_name || "",
      u.business_name || "",
      u.platform,
      u.reporting ? "Reportando" : "No reporta",
    ]);

    // toCsv escapa comillas y fórmulas, y downloadCsv agrega el BOM: antes un
    // nombre con comillas corría las columnas y Excel mostraba «RazÃ³n Social».
    downloadCsv(filename, toCsv(headers, rows));
  };

  const companyName = user?.company_name || "Mi Compañía";

  return (
    <div className={`w-full h-full overflow-auto ${themeClasses.bg} p-4 sm:p-6`}>
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className={`text-2xl sm:text-3xl font-bold ${themeClasses.text}`}>
          {t("entregaServicio.pageTitle")}
        </h1>
        <p className={`text-sm ${themeClasses.textSecondary}`}>
          {t("entregaServicio.pageSubtitle")} - {companyName}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 sm:mb-8">
        {/* Total Units */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                {t("entregaServicio.totalUnits")}
              </p>
              <h2 className={`text-3xl sm:text-4xl font-bold ${themeClasses.text} mt-2`}>
                {allUnits.length}
              </h2>
            </div>
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-blue-500/10 rounded-full flex items-center justify-center">
              <i className="ri-truck-line text-2xl text-blue-500"></i>
            </div>
          </div>
        </div>

        {/* Units Reporting */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                {t("entregaServicio.unitsReporting")}
              </p>
              <h2 className={`text-3xl sm:text-4xl font-bold text-green-500 mt-2`}>
                {reporting.length}
              </h2>
            </div>
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-green-500/10 rounded-full flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-2xl text-green-500"></i>
            </div>
          </div>
        </div>

        {/* Units Not Reporting */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                {t("entregaServicio.unitsNotReporting")}
              </p>
              <h2 className={`text-3xl sm:text-4xl font-bold text-red-500 mt-2`}>
                {notReporting.length}
              </h2>
            </div>
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-500/10 rounded-full flex items-center justify-center">
              <i className="ri-close-circle-line text-2xl text-red-500"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Cards */}
      {allPlatforms.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 sm:mb-8">
          {allPlatforms.map((platform) => {
            const reportingCount = platformCountsReporting[platform] || 0;
            const notReportingCount = platformCountsNotReporting[platform] || 0;
            const totalCount = reportingCount + notReportingCount;
            const effectivenessPercent = totalCount > 0 ? Math.round((reportingCount / totalCount) * 100) : 0;

            const platformColors: Record<string, { bg: string; border: string; icon: string; text: string }> = {
              v5: { bg: "bg-blue-500/10", border: "border-blue-500", icon: "ri-cloud-line", text: "text-blue-500" },
              wialon: { bg: "bg-purple-500/10", border: "border-purple-500", icon: "ri-map-pin-line", text: "text-purple-500" },
              mapon: { bg: "bg-orange-500/10", border: "border-orange-500", icon: "ri-navigation-line", text: "text-orange-500" },
            };

            const colors = platformColors[platform] || { bg: "bg-gray-500/10", border: "border-gray-500", icon: "ri-grid-line", text: "text-gray-500" };

            return (
              <div key={platform} className={`${themeClasses.cardBg} border ${colors.border} rounded-xl p-4 sm:p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-semibold ${themeClasses.text} uppercase`}>{platform}</h3>
                  <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center`}>
                    <i className={`${colors.icon} text-lg ${colors.text}`}></i>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${themeClasses.textSecondary}`}>{t("entregaServicio.total")}</span>
                    <span className={`text-lg font-bold ${themeClasses.text}`}>{totalCount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${themeClasses.textSecondary}`}>{t("entregaServicio.reporting")}</span>
                    <span className={`text-lg font-bold text-green-500`}>{reportingCount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${themeClasses.textSecondary}`}>{t("entregaServicio.notReporting")}</span>
                    <span className={`text-lg font-bold text-red-500`}>{notReportingCount}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className={`text-xs font-medium ${themeClasses.textSecondary}`}>{t("entregaServicio.availability")}</span>
                      <span className={`text-sm font-bold ${effectivenessPercent >= 80 ? "text-green-500" : effectivenessPercent >= 60 ? "text-yellow-500" : "text-red-500"}`}>
                        {effectivenessPercent}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-6 sm:space-y-8">
        {/* Unidades Reportando */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl overflow-hidden`}>
          <div className={`p-4 sm:p-6 border-b ${themeClasses.cardBorder}`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h3 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
                  {t("entregaServicio.unitsReporting")}
                </h3>
                <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
                  {filteredReporting.length} {t("entregaServicio.of")} {reporting.length} {t("entregaServicio.activeUnits")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  placeholder={t("entregaServicio.searchPlaceholder")}
                  value={searchReporting}
                  onChange={(e) => {
                    setSearchReporting(e.target.value);
                    setCurrentPageReporting(1);
                  }}
                  className={`px-3 py-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.bg} ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500`}
                />
                {platformsReporting.length > 1 && (
                  <select
                    value={platformFilterReporting}
                    onChange={(e) => {
                      setPlatformFilterReporting(e.target.value);
                      setCurrentPageReporting(1);
                    }}
                    className={`px-3 py-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.bg} ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  >
                    <option value="">{t("entregaServicio.allPlatforms")}</option>
                    {platformsReporting.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  onClick={() => downloadTableAsCSV(filteredReporting, "unidades_reportando")}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <i className="ri-download-2-line"></i>
                  {t("entregaServicio.download")}
                </button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            {reportingQuery.isLoading ? (
              <div className="p-4">
                <TableSkeleton />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className={`border-b ${themeClasses.cardBorder} ${themeClasses.text}`}>
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">{t("entregaServicio.imei")}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t("entregaServicio.deviceName")}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t("entregaServicio.user")}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t("entregaServicio.businessName")}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t("entregaServicio.platform")}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t("entregaServicio.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReporting.length > 0 ? (
                    getReportingPage().map((unit: ReportingUnit) => (
                      <tr key={unit.imei} className={`border-b ${themeClasses.cardBorder} ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}>
                        <td className={`px-4 py-3 font-mono text-xs ${themeClasses.text}`}>{unit.imei}</td>
                        <td className={`px-4 py-3 ${themeClasses.text}`}>{unit.device_name || "—"}</td>
                        <td className={`px-4 py-3 ${themeClasses.text}`}>{unit.user_name || "—"}</td>
                        <td className={`px-4 py-3 ${themeClasses.text}`}>{unit.business_name || "—"}</td>
                        <td className={`px-4 py-3 ${themeClasses.text}`}>{unit.platform}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 text-xs font-medium">
                            <i className="ri-circle-fill text-xs"></i>
                            {t("entregaServicio.reporting_status")}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className={`px-4 py-8 text-center ${themeClasses.textSecondary}`}>
                        {reporting.length === 0 ? t("entregaServicio.noUnitsReporting") : t("entregaServicio.noResults")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          <Pagination
            currentPage={currentPageReporting}
            totalPages={totalPagesReporting}
            onPageChange={setCurrentPageReporting}
          />
        </div>

        {/* Unidades No Reportando */}
        <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl overflow-hidden`}>
          <div className={`p-4 sm:p-6 border-b ${themeClasses.cardBorder}`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h3 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
                  {t("entregaServicio.unitsNotReporting")}
                </h3>
                <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
                  {filteredNotReporting.length} {t("entregaServicio.of")} {notReporting.length} {t("entregaServicio.inactiveUnits")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  placeholder={t("entregaServicio.searchPlaceholder")}
                  value={searchNotReporting}
                  onChange={(e) => {
                    setSearchNotReporting(e.target.value);
                    setCurrentPageNotReporting(1);
                  }}
                  className={`px-3 py-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.bg} ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500`}
                />
                {platformsNotReporting.length > 1 && (
                  <select
                    value={platformFilterNotReporting}
                    onChange={(e) => {
                      setPlatformFilterNotReporting(e.target.value);
                      setCurrentPageNotReporting(1);
                    }}
                    className={`px-3 py-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.bg} ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  >
                    <option value="">{t("entregaServicio.allPlatforms")}</option>
                    {platformsNotReporting.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  onClick={() => downloadTableAsCSV(filteredNotReporting, "unidades_no_reportando")}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <i className="ri-download-2-line"></i>
                  {t("entregaServicio.download")}
                </button>
                <button
                  onClick={() => setShowReportModal(true)}
                  disabled={filteredNotReporting.length === 0}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-mail-send-line"></i>
                  {t("entregaServicio.report")}
                </button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            {notReportingQuery.isLoading ? (
              <div className="p-4">
                <TableSkeleton />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className={`border-b ${themeClasses.cardBorder} ${themeClasses.text}`}>
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">{t("entregaServicio.imei")}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t("entregaServicio.deviceName")}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t("entregaServicio.user")}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t("entregaServicio.businessName")}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t("entregaServicio.platform")}</th>
                    <th className="px-4 py-3 text-left font-semibold">{t("entregaServicio.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNotReporting.length > 0 ? (
                    getNotReportingPage().map((unit: ReportingUnit) => (
                      <tr key={unit.imei} className={`border-b ${themeClasses.cardBorder} ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}>
                        <td className={`px-4 py-3 font-mono text-xs ${themeClasses.text}`}>{unit.imei}</td>
                        <td className={`px-4 py-3 ${themeClasses.text}`}>{unit.device_name || "—"}</td>
                        <td className={`px-4 py-3 ${themeClasses.text}`}>{unit.user_name || "—"}</td>
                        <td className={`px-4 py-3 ${themeClasses.text}`}>{unit.business_name || "—"}</td>
                        <td className={`px-4 py-3 ${themeClasses.text}`}>{unit.platform}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 text-xs font-medium">
                            <i className="ri-circle-fill text-xs"></i>
                            {t("entregaServicio.not_reporting_status")}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className={`px-4 py-8 text-center ${themeClasses.textSecondary}`}>
                        {notReporting.length === 0 ? t("entregaServicio.allUnitsReporting") : t("entregaServicio.noResults")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          <Pagination
            currentPage={currentPageNotReporting}
            totalPages={totalPagesNotReporting}
            onPageChange={setCurrentPageNotReporting}
          />
        </div>
      </div>

      {/* Modal de Reporte */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.cardBg} rounded-lg shadow-lg max-w-md w-full`}>
            <div className={`p-6 border-b ${themeClasses.cardBorder}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-xl font-bold ${themeClasses.text}`}>
                  {t("entregaServicio.modal.title")}
                </h2>
                <button
                  onClick={() => setShowReportModal(false)}
                  className={`text-2xl ${themeClasses.textSecondary} hover:${themeClasses.text}`}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className={`p-3 rounded-lg ${isDarkMode ? "bg-orange-900/20" : "bg-orange-100"}`}>
                <p className={`text-sm ${isDarkMode ? "text-orange-200" : "text-orange-800"}`}>
                  <strong>{t("entregaServicio.modal.unitsToReport")}</strong> {filteredNotReporting.length}
                </p>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => setShowReportModal(false)}
                  className={`flex-1 px-4 py-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.text} hover:${isDarkMode ? "bg-gray-800" : "bg-gray-50"} transition-colors text-sm font-medium`}
                >
                  {t("entregaServicio.modal.cancel")}
                </button>
                <button
                  onClick={handleSendReport}
                  disabled={isReporting}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isReporting ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      {t("entregaServicio.modal.sending")}
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line"></i>
                      {t("entregaServicio.modal.send")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
